import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Modal, Button, Input } from '../design/primitives';
import { Alert } from './ui';
import { apiClient } from '../api/apiConfig';
import { EmailAccount } from '../types/email';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  ListNumberedIcon,
  LinkIcon,
} from './ui/RichTextIcons';

interface ComposeEmailProps {
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
  replyTo?: {
    id: number;
    subject: string;
    sender: string;
    sender_name: string;
    body: string;
    account_detail?: { id: number; email_address: string } | null;
    cc_recipients?: string;
    recipients?: string;
    replyAll?: boolean;
  } | null;
  accounts: EmailAccount[];
}

const ToolbarButton: React.FC<{
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={e => { e.preventDefault(); onClick(); }}
    title={title}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: SPACING[4],
      borderRadius: '4px',
      color: COLORS.text.secondary,
      display: 'flex',
      alignItems: 'center',
    }}
  >
    {children}
  </button>
);

const ComposeEmail: React.FC<ComposeEmailProps> = ({ isOpen, onClose, onSent, replyTo, accounts }) => {
  const [fromAccountId, setFromAccountId] = useState<string>('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Pre-fill for replies
  useEffect(() => {
    if (replyTo) {
      setTo(replyTo.sender);
      setSubject(replyTo.subject.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`);
      if (replyTo.account_detail?.id) {
        setFromAccountId(String(replyTo.account_detail.id));
      }

      // Reply All: build CC list from original CC + TO recipients (excluding our own account)
      if (replyTo.replyAll) {
        const myEmail = replyTo.account_detail?.email_address?.toLowerCase() || '';
        const senderEmail = replyTo.sender.toLowerCase();
        const allCc = new Set<string>();

        // Add original TO recipients (except our account and the sender who goes in TO)
        if (replyTo.recipients) {
          replyTo.recipients.split(',').forEach(r => {
            const email = r.replace(/.*<([^>]+)>.*/, '$1').trim().toLowerCase();
            if (email && email !== myEmail && email !== senderEmail) {
              allCc.add(r.trim());
            }
          });
        }
        // Add original CC recipients (except our account and sender)
        if (replyTo.cc_recipients) {
          replyTo.cc_recipients.split(',').forEach(r => {
            const email = r.replace(/.*<([^>]+)>.*/, '$1').trim().toLowerCase();
            if (email && email !== myEmail && email !== senderEmail) {
              allCc.add(r.trim());
            }
          });
        }
        setCc(Array.from(allCc).join(', '));
      }

      // Set reply content after a tick so the editor is mounted
      setTimeout(() => {
        if (editorRef.current) {
          const quoted = stripHtml(replyTo.body).slice(0, 500);
          editorRef.current.innerHTML = `<br><br><div style="border-left: 2px solid ${COLORS.border.default}; padding-left: 8px; color: ${COLORS.text.tertiary};">--- Original message from ${replyTo.sender_name || replyTo.sender} ---<br>${quoted.replace(/\n/g, '<br>')}</div>`;
          // Place cursor at beginning
          const sel = window.getSelection();
          if (sel) {
            sel.selectAllChildren(editorRef.current);
            sel.collapseToStart();
          }
        }
      }, 50);
    } else {
      resetForm();
    }
  }, [replyTo, isOpen]);

  // Auto-select default account if none selected
  useEffect(() => {
    if (!fromAccountId && accounts.length > 0) {
      const defaultAccount = accounts.find(a => a.is_default) || accounts[0];
      setFromAccountId(String(defaultAccount.id));
    }
  }, [accounts, fromAccountId]);

  const resetForm = () => {
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    if (editorRef.current) editorRef.current.innerHTML = '';
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    setFromAccountId('');
    onClose();
  };

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) execCommand('createLink', url);
  }, [execCommand]);

  const handleSend = async () => {
    if (!fromAccountId || !to || !subject) {
      setError('From account, To, and Subject are required.');
      return;
    }

    const bodyHtml = editorRef.current?.innerHTML || '';
    const bodyText = editorRef.current?.innerText || '';

    if (!bodyText.trim()) {
      setError('Email body cannot be empty.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const wrappedHtml = `<div style="font-family: sans-serif; font-size: 14px; line-height: 1.5;">${bodyHtml}</div>`;

      await apiClient.post('/api/emails/send/', {
        from_account_id: parseInt(fromAccountId),
        to,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject,
        body_html: wrappedHtml,
        body_text: bodyText,
        in_reply_to_email_id: replyTo?.id || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        handleClose();
        onSent?.();
      }, 1000);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to send email';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const accountOptions = accounts.map(a => ({
    value: String(a.id),
    label: `${a.email_address}${a.business_name ? ` (${a.business_name})` : ''}`,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={replyTo ? (replyTo.replyAll ? 'Reply All' : 'Reply') : 'Compose Email'}
      footer={
        <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={handleClose} disabled={sending}>Cancel</Button>
          <Button variant="primary" onClick={handleSend} disabled={sending || success}>
            {sending ? 'Sending...' : success ? 'Sent!' : 'Send'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">Email sent successfully!</Alert>}

        <div>
          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>From</label>
          <select
            value={fromAccountId}
            onChange={e => setFromAccountId(e.target.value)}
            style={{
              width: '100%',
              padding: `${SPACING[8]} ${SPACING[12]}`,
              backgroundColor: COLORS.bg.base,
              color: COLORS.text.primary,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.size.sm,
              outline: 'none',
            }}
          >
            {accountOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>To</label>
          <Input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" />
        </div>

        <div style={{ display: 'flex', gap: SPACING[8] }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>CC</label>
            <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@example.com" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>BCC</label>
            <Input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="bcc@example.com" />
          </div>
        </div>

        <div>
          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>Subject</label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject" />
        </div>

        <div>
          <label style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[4], display: 'block' }}>Body</label>
          {/* Formatting toolbar */}
          <div style={{
            display: 'flex',
            gap: SPACING[4],
            padding: `${SPACING[4]} ${SPACING[8]}`,
            border: `1px solid ${COLORS.border.default}`,
            borderBottom: 'none',
            borderRadius: `${RADIUS} ${RADIUS} 0 0`,
            backgroundColor: COLORS.bg.panel,
          }}>
            <ToolbarButton onClick={() => execCommand('bold')} title="Bold (Ctrl+B)">
              <BoldIcon />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('italic')} title="Italic (Ctrl+I)">
              <ItalicIcon />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('underline')} title="Underline (Ctrl+U)">
              <UnderlineIcon />
            </ToolbarButton>
            <div style={{ width: 1, backgroundColor: COLORS.border.default, margin: `0 ${SPACING[4]}` }} />
            <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet list">
              <ListBulletIcon />
            </ToolbarButton>
            <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered list">
              <ListNumberedIcon />
            </ToolbarButton>
            <div style={{ width: 1, backgroundColor: COLORS.border.default, margin: `0 ${SPACING[4]}` }} />
            <ToolbarButton onClick={insertLink} title="Insert link">
              <LinkIcon />
            </ToolbarButton>
          </div>
          {/* Editable area */}
          <div
            ref={editorRef}
            contentEditable
            style={{
              minHeight: '200px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: SPACING[12],
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: `0 0 ${RADIUS} ${RADIUS}`,
              backgroundColor: COLORS.bg.base,
              color: COLORS.text.primary,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.size.sm,
              lineHeight: '1.5',
              outline: 'none',
            }}
            onKeyDown={e => {
              if (e.key === 'Tab') {
                e.preventDefault();
                execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
              }
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export default ComposeEmail;
